import { useEffect, useMemo, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { ArrowLeft, CheckCircle, RocketLaunch, Copy } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export default function SimilarStep({ step, setStep, projectId, pack, specs, onFinalized }) {
  const [similar, setSimilar] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [vstat, setVstat] = useState(null);

  useEffect(() => {
    if (step === 4 && projectId) {
      api.get(`/projects/${projectId}/similar`).then(({ data }) => setSimilar(data)).catch((e) => toast.error(fmtErr(e)));
    }
  }, [step, projectId]);

  const loadVariants = () => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/finalize-status`).then(({ data }) => setVstat(data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (step === 5) loadVariants(); }, [step, projectId]);

  const requiredCheck = useMemo(() => {
    const fields = (pack?.sections || []).flatMap((s) => s.fields.filter((f) => f.required));
    return fields.map((f) => ({ ...f, filled: specs[f.id] !== undefined && specs[f.id] !== null && specs[f.id] !== "" }));
  }, [pack, specs]);

  const missing = requiredCheck.filter((f) => !f.filled);

  const applyBaseline = async (sourceId) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/baseline`, { source_id: sourceId });
      toast.success(`Baseline applied — ${data.copied} parameters copied`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const finalize = async (scope = "variant") => {
    setFinalizing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/finalize`, null, { params: { scope } });
      const tags = data.finalized_variants || [];
      toast.success(tags.length
        ? `${tags.join(", ")} finalized — ${data.finalized_count}/${data.variant_count} variant(s) ready for the Design Engine`
        : "Finalized — specs locked, client rules loaded. SCM auto-check is running in the background.");
      loadVariants();
      onFinalized(projectId);
    } catch (e) {
      const missingList = e?.response?.data?.missing;
      toast.error(missingList ? `Missing critical fields: ${missingList.join(", ")}` : fmtErr(e), { duration: 8000 });
    } finally { setFinalizing(false); }
  };

  if (step === 4) {
    return (
      <div className="space-y-4" data-testid="np-step-similar">
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
          <div className="text-sm font-medium text-[var(--c-text)]">Similar Past Projects</div>
          <div className="text-xs text-[var(--c-muted)] mt-0.5">Ranked by matching spec parameters in {pack?.name || "this industry"}</div>
        </div>
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="similar-table">
            <thead>
              <tr className="border-b border-[var(--c-border)]">
                {["Project", "Product", "Client", "Similarity", "Matched Parameters", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(similar || []).map((p) => (
                <tr key={p.id} className="border-b border-[var(--c-border)]/60 last:border-0" data-testid={`similar-row-${p.project_code}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--c-muted)]">{p.project_code}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--c-text)]">{p.product_summary}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--c-muted)]">{p.client_name}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-[var(--c-border)] rounded-sm overflow-hidden">
                        <div className="h-full bg-[var(--c-green)]" style={{ width: `${p.similarity}%` }} />
                      </div>
                      <span className="font-mono text-xs text-[var(--c-text)]" data-testid={`similarity-${p.project_code}`}>{p.similarity}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[10px] text-[var(--c-muted)] max-w-56 truncate">{(p.matched || []).join(", ") || "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" data-testid={`baseline-${p.project_code}`} onClick={() => applyBaseline(p.id)}
                      className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-7 text-xs">
                      <Copy size={12} className="mr-1" /> Use as Baseline
                    </Button>
                  </td>
                </tr>
              ))}
              {similar && similar.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[var(--c-muted)]" data-testid="similar-none">No past projects in this industry yet.</td></tr>
              )}
              {similar === null && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[var(--c-muted)]">Searching…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between">
          <Button variant="outline" data-testid="np-back-5" onClick={() => setStep(3)}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
            <ArrowLeft size={14} className="mr-1.5" /> Back
          </Button>
          <Button data-testid="np-continue-5" onClick={() => setStep(5)}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="np-step-finalize">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
        <div className="text-sm font-medium text-[var(--c-text)] mb-4">Critical Field Validation · {pack?.name}</div>
        <div className="space-y-1.5" data-testid="finalize-checklist">
          {requiredCheck.map((f) => (
            <div key={f.id} className="flex items-center gap-2.5 text-xs" data-testid={`finalize-field-${f.id}`}>
              {f.filled
                ? <CheckCircle size={14} weight="fill" className="text-[var(--c-green)]" />
                : <span className="w-3.5 h-3.5 rounded-full border border-[var(--c-red)] inline-block" />}
              <span className={f.filled ? "text-[var(--c-text)]" : "text-[var(--c-red)]"}>{f.label}</span>
              <span className="font-mono text-[10px] text-[var(--c-muted)]">{f.id}</span>
              {f.filled && <span className="font-mono text-[10px] text-[var(--c-accent)] ml-auto">{String(specs[f.id])} {f.unit}</span>}
            </div>
          ))}
          {requiredCheck.length === 0 && <div className="text-xs text-[var(--c-muted)]">No required fields in this pack.</div>}
        </div>
        {missing.length > 0 && (
          <div className="mt-4 text-xs text-[var(--c-amber)] border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10 rounded-sm px-3 py-2" data-testid="finalize-missing-warning">
            {missing.length} critical field(s) empty — finalization will be blocked until filled.
          </div>
        )}
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5 text-xs text-[var(--c-muted)] space-y-1.5" data-testid="finalize-actions-list">
        <div>On finalize, the platform will: set status <span className="font-mono text-[var(--c-text)]">specs_extracted</span>, load the client rules snapshot, and trigger the SCM auto-check in the background (draft inquiries appear in SCM shortly).</div>
      </div>
      {vstat && vstat.count > 1 && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-hidden"
          data-testid="finalize-variants">
          <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs text-[var(--c-text)]">
            {vstat.count} variants in this project — finalize each variant you want to engineer.
            <span className="text-[var(--c-muted)]"> Only finalized variants run the Design Engine,
            Validation and Drawing Studio.</span>
            <span className="ml-2 font-mono text-[10px] text-[var(--c-accent)]">
              {vstat.finalized_count}/{vstat.count} finalized
            </span>
          </div>
          {vstat.variants.map((v) => (
            <div key={v.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--c-border)] last:border-0"
              data-testid={`finalize-variant-${v.tag}`}>
              <span className="font-mono text-[10px] text-[var(--c-accent)] w-14">{v.tag}</span>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-[var(--c-text)] truncate">{v.name}</div>
                <div className="text-[10px] font-mono text-[var(--c-muted)]">
                  {v.quantity > 1 ? `x${v.quantity} · ` : ""}
                  {v.ready ? "all critical fields present" : `${v.missing.length} critical field(s) empty`}
                  {v.active ? " · active" : ""}
                </div>
              </div>
              {v.finalized ? (
                <span className="flex items-center gap-1 text-[10px] font-mono text-[var(--c-green)]"
                  data-testid={`finalized-badge-${v.tag}`}>
                  <CheckCircle size={12} weight="fill" /> finalized
                </span>
              ) : v.active ? (
                <Button size="sm" data-testid={`finalize-variant-btn-${v.tag}`} disabled={finalizing || !v.ready}
                  onClick={() => finalize("variant")}
                  className="h-7 bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white text-xs rounded-sm">
                  Finalize this variant
                </Button>
              ) : (
                <span className="text-[10px] text-[var(--c-muted)]">
                  switch to {v.tag} above to finalize
                </span>
              )}
            </div>
          ))}
          <div className="px-4 py-2.5 flex justify-end">
            <Button size="sm" variant="outline" data-testid="finalize-all-variants"
              disabled={finalizing || !vstat.variants.some((v) => v.ready && !v.finalized)}
              onClick={() => finalize("all")}
              className="h-7 border-[var(--c-border)] text-[var(--c-text)] text-xs rounded-sm">
              Finalize all complete variants
            </Button>
          </div>
        </div>
      )}
      <div className="flex justify-between">
        <Button variant="outline" data-testid="np-back-6" onClick={() => setStep(4)}
          className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <Button data-testid="np-create-btn" onClick={() => finalize("variant")} disabled={finalizing}
          className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm">
          <RocketLaunch size={14} className="mr-1.5" />
          {finalizing ? "Finalizing…"
            : vstat && vstat.count > 1
              ? `Finalize ${vstat.variants.find((v) => v.active)?.tag || "this variant"}`
              : "Finalize Project"}
        </Button>
      </div>
    </div>
  );
}
